import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatImageUrl } from '../utils/helpers';
import { FileText, Headphones, Calendar } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, blog, podcast

  useEffect(() => {
    fetchPosts();
  }, [filter]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { type: filter } : {};
      const response = await axios.get(`${API_URL}/posts`, { params });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="relative min-h-screen pt-24 pb-12">
      <canvas id="starfield" className="fixed inset-0 z-0 pointer-events-none"></canvas>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="section-title text-4xl md:text-5xl mb-4">
            <span>Blog & Podcast</span>
          </h1>
          <p className="text-gray-300 text-lg">Gondolatok, történetek és tudás</p>
        </div>

        {/* Filter */}
        <div className="flex justify-center gap-3 mb-8">
          <button
            onClick={() => setFilter('all')}
            className={`px-6 py-2 rounded-lg transition ${
              filter === 'all'
                ? 'bg-yellow-200 text-black font-semibold'
                : 'bg-white/5 text-white hover:bg-white/10'
            }`}
          >
            Összes
          </button>
          <button
            onClick={() => setFilter('blog')}
            className={`px-6 py-2 rounded-lg transition flex items-center gap-2 ${
              filter === 'blog'
                ? 'bg-yellow-200 text-black font-semibold'
                : 'bg-white/5 text-white hover:bg-white/10'
            }`}
          >
            <FileText className="w-4 h-4" />
            Blog
          </button>
          <button
            onClick={() => setFilter('podcast')}
            className={`px-6 py-2 rounded-lg transition flex items-center gap-2 ${
              filter === 'podcast'
                ? 'bg-yellow-200 text-black font-semibold'
                : 'bg-white/5 text-white hover:bg-white/10'
            }`}
          >
            <Headphones className="w-4 h-4" />
            Podcast
          </button>
        </div>

        {/* Posts Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-200"></div>
            <p className="mt-4 text-gray-400">Betöltés...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 glass rounded-2xl">
            <FileText className="w-16 h-16 text-yellow-200/50 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Még nincs bejegyzés</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                className="group glass rounded-2xl overflow-hidden hover:border-yellow-200/50 transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Cover Image */}
                <div className="relative h-48 overflow-hidden bg-gradient-to-br from-blue-900/20 to-black">
                  {post.cover_image ? (
                    <img
                      src={formatImageUrl(post.cover_image)}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {post.type === 'podcast' ? (
                        <Headphones className="w-16 h-16 text-purple-200/30" />
                      ) : (
                        <FileText className="w-16 h-16 text-blue-200/30" />
                      )}
                    </div>
                  )}
                  
                  {/* Type Badge */}
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 backdrop-blur-sm border rounded-full text-xs font-semibold ${
                      post.type === 'podcast'
                        ? 'bg-purple-500/60 border-purple-400/30 text-white'
                        : 'bg-blue-500/60 border-blue-400/30 text-white'
                    }`}>
                      {post.type === 'podcast' ? '🎧 Podcast' : '📝 Blog'}
                    </span>
                  </div>
                </div>

                {/* Post Info */}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(post.created_at)}</span>
                  </div>

                  <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-yellow-200 transition line-clamp-2">
                    {post.title}
                  </h3>
                  
                  {post.excerpt && (
                    <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                  )}

                  <span className="text-sm text-yellow-200 group-hover:text-yellow-100 transition">
                    Tovább olvasom →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .section-title {
          font-family: "Great Vibes", cursive;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </div>
  );
};

export default Blog;
